import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FiHome, FiUsers, FiFileText, FiSend, FiUser, FiEdit, FiLogOut, FiMenu, FiX, FiChevronLeft, FiChevronRight, FiGrid, FiSettings, FiActivity, FiShoppingCart, FiAlertTriangle, FiAlertOctagon, FiGitBranch, FiMessageSquare, FiRadio, FiBookOpen, FiSmartphone, FiHeadphones, FiBell } from 'react-icons/fi';
import { isFluxDomain } from '../utils/fluxTheme';
import './Sidebar.css';

function Sidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [supportUnread, setSupportUnread] = useState(0);
  const [notifUnread,   setNotifUnread]   = useState(0);

  // Determine role from URL
  const isAdmin = location.pathname.includes('/admin');
  const isAgent = location.pathname.includes('/agent');
  const role = isAdmin ? 'Admin' : isAgent ? 'Agent' : 'User';

  // Get user info from token
  const [userInfo, setUserInfo] = useState({
    email: '',
    name: '',
    memberEmail: '',
    memberName: '',
    groupLocationName: '',
    isGroupMember: false
  });

  React.useEffect(() => {
    const token = isAdmin ? localStorage.getItem('adminToken') : localStorage.getItem('agentToken');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        setUserInfo({
          email: payload.email || '',
          name: payload.name || '',
          memberEmail: payload.memberEmail || '',
          memberName: payload.memberName || '',
          groupLocationName: payload.groupLocationName || '',
          isGroupMember: !!payload.memberEmail
        });
      } catch (error) {
        console.error('Error parsing token:', error);
      }
    }
  }, [isAdmin]);

  // Poll support unread count every 30s — only when on an admin or agent page
  React.useEffect(() => {
    const fetchUnread = async () => {
      try {
        // Only poll if we're actually on an admin or agent page
        if (!isAdmin && !isAgent) return;
        const token = isAdmin
          ? localStorage.getItem('adminToken')
          : localStorage.getItem('agentToken');
        // Guard against null / "null" / "undefined" — all mean no session
        if (!token || token === 'null' || token === 'undefined') return;
        const endpoint = isAdmin
          ? '/api/support/admin/conversations'
          : '/api/support/agent/conversation';
        const res = await fetch(endpoint, { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok) return;
        const data = await res.json();
        if (isAdmin) {
          const total = (data.conversations || []).reduce((s, c) => s + (c.adminUnread || 0), 0);
          setSupportUnread(total);
        } else {
          setSupportUnread(data.conversation?.agentUnread || 0);
        }
      } catch (_) {}
    };
    fetchUnread();
    const id = setInterval(fetchUnread, 30000);
    return () => clearInterval(id);
  }, [isAdmin]);

  // Poll agent notification unread count (agent side only)
  React.useEffect(() => {
    if (isAdmin || !isAgent) return;
    const fetchNotif = async () => {
      try {
        const token = localStorage.getItem('agentToken');
        if (!token || token === 'null' || token === 'undefined') return;
        const res = await fetch('/api/admin-notifications/agent?limit=50', { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok) return;
        const data = await res.json();
        setNotifUnread((data.notifications || []).filter(n => !n.readAt).length);
      } catch (_) {}
    };
    fetchNotif();
    const id = setInterval(fetchNotif, 60000);
    return () => clearInterval(id);
  }, [isAdmin, isAgent]);

  const getInitials = (text) => {
    if (!text) return '??';
    return text.substring(0, 2).toUpperCase();
  };

  // Determine display name and email with safe fallbacks
  const displayName = userInfo.isGroupMember
    ? (userInfo.memberName || 'Member')
    : (userInfo.name || (userInfo.email ? userInfo.email.split('@')[0] : 'User'));
  const displayEmail = userInfo.isGroupMember ? userInfo.memberEmail : userInfo.email;

  const toggleSidebar = () => {
    setIsCollapsed(!isCollapsed);
  };

  const toggleMobileMenu = () => {
    setIsMobileOpen(!isMobileOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileOpen(false);
  };

  const handleLogout = () => {
    if (isAdmin) {
      localStorage.removeItem('adminToken');
      localStorage.removeItem('selectedCampaign');
      window.location.href = '/admin/login';
    } else if (isAgent) {
      localStorage.removeItem('agentToken');
      localStorage.removeItem('selectedCampaign');
      window.location.href = '/agent/login';
    }
  };

  const isActive = (path) => {
    // Exact match for base paths
    if (path === '/admin' || path === '/agent') {
      return location.pathname === path;
    }

    // Special handling for /agent/request vs /agent/requests
    if (path === '/agent/request' || path === '/admin/agents') {
      // Must match exactly or with trailing slash only
      return location.pathname === path || location.pathname === `${path}/`;
    }

    // For other paths, use startsWith but ensure it's not a partial match
    // by checking if next character is / or end of string
    if (location.pathname === path) return true;
    if (location.pathname.startsWith(path + '/')) return true;
    return false;
  };

  // Get selected campaign
  const selectedCampaign = localStorage.getItem('selectedCampaign') || 'medicare';

  // Check if agent has multiple campaigns and get campaign list
  const [hasMultipleCampaigns, setHasMultipleCampaigns] = useState(false);
  const [agentCampaigns, setAgentCampaigns] = useState([]);

  React.useEffect(() => {
    if (isAgent) {
      const token = localStorage.getItem('agentToken');
      if (token) {
        try {
          const payload = JSON.parse(atob(token.split('.')[1]));
          setHasMultipleCampaigns(payload.campaignCount > 1);
          setAgentCampaigns(payload.campaigns || []);
        } catch (error) {
          console.error('Error parsing token:', error);
        }
      }
    }
  }, [isAgent]);

  // Menu items based on role - matching actual app routes
  const adminMenuItems = [
    { path: '/admin/dashboard', icon: <FiHome />, label: 'Dashboard' },
    { path: '/admin/agents', icon: <FiUsers />, label: 'Agents' },
    { path: '/admin/pending-agents', icon: <FiUsers />, label: 'Pending Agents', badge: true },
    { path: '/admin/configs', icon: <FiSettings />, label: 'Bulk Configs' },
    { path: '/admin/requests', icon: <FiFileText />, label: 'Change Requests' },
    { path: '/admin/leads', icon: <FiSend />, label: 'Opportunities Distribution' },
    { path: '/admin/survey-tracking', icon: <FiActivity />, label: 'Survey Tracking' },
    { path: '/admin/orders', icon: <FiShoppingCart />, label: 'Opportunities Orders' },
    { path: '/admin/rate-limits', icon: <FiAlertTriangle />, label: 'Rate Limits' },
    { path: '/admin/disqualified-leads', icon: <FiAlertOctagon />, label: 'Disqualified Leads' },
    { path: '/admin/app-stats', icon: <FiSmartphone />, label: 'App Usage Stats' },
    { path: '/admin/push-leads', icon: <FiBell />, label: 'Push Notifications' },
    { path: '/admin/support',        icon: <FiHeadphones />, label: 'Agent Support', supportBadge: true },
    { path: '/admin/notifications',  icon: <FiBell />,       label: 'Send Notifications' },
    { path: '/admin/ghl-subaccounts', icon: <FiActivity />,  label: 'GHL Subaccounts' },
  ];

  const agentMenuItems = [
    { path: '/agent', icon: <FiHome />, label: 'Dashboard' },
    { path: '/agent/opportunities', icon: <FiSend />, label: 'Opportunities' },
    { path: '/agent/pipelines', icon: <FiGitBranch />, label: 'Pipelines' },
    { path: '/agent/leads',      icon: <FiMessageSquare />, label: 'Conversations' },
    { path: '/agent/broadcasts', icon: <FiRadio />,         label: 'Client Broadcasts' },
    { path: '/agent/profile', icon: <FiUser />, label: 'My Profile' },
    { path: '/agent/requests', icon: <FiFileText />, label: 'My Change Requests' },
    { path: '/agent/request', icon: <FiEdit />, label: 'Request Changes' },
    { path: '/agent/orders', icon: <FiShoppingCart />, label: 'Opportunities Orders' },
    { path: '/agent/walkthrough', icon: <FiBookOpen />, label: 'Portal Walkthrough' },
    { path: '/agent/app-stats', icon: <FiSmartphone />, label: 'App Usage Stats' },
    { path: '/agent/support',        icon: <FiHeadphones />, label: 'Support',          supportBadge: true },
    { path: '/agent/notifications',  icon: <FiBell />,       label: 'Notifications',    notifBadge: true },
  ];

  // Orders routes are only available on Flux domains
  const fluxOnly = isFluxDomain();
  const menuItems = (isAdmin ? adminMenuItems : agentMenuItems.filter(item => fluxOnly || !item.path.endsWith('/orders')))
    ;

  return (
    <>
      {/* Mobile Menu Button */}
      <button
        className="mobile-menu-btn"
        onClick={toggleMobileMenu}
        aria-label="Toggle menu"
      >
        {isMobileOpen ? <FiX size={24} /> : <FiMenu size={24} />}
      </button>

      {/* Overlay for mobile */}
      {isMobileOpen && (
        <div
          className="sidebar-overlay"
          onClick={closeMobileMenu}
        />
      )}

      {/* Sidebar */}
      <aside className={`sidebar ${isCollapsed ? 'collapsed' : ''} ${isMobileOpen ? 'mobile-open' : ''}`}>
        {/* Sidebar Header - Campaign Selector */}
        <div className="sidebar-header">
          <button
            className="sidebar-logo campaign-header-btn"
            onClick={() => {
              if (isAdmin) {
                navigate('/admin');
              } else if (hasMultipleCampaigns) {
                localStorage.removeItem('selectedCampaign');
                navigate('/agent/select-campaign');
              }
              closeMobileMenu();
            }}
            title={isAdmin || hasMultipleCampaigns ? "Switch Campaign" : "Current Campaign"}
            style={{ cursor: isAdmin || hasMultipleCampaigns ? 'pointer' : 'default' }}
          >
            <FiGrid style={{ marginRight: isCollapsed ? '0' : '8px', fontSize: '20px' }} />
            {!isCollapsed && <h2>{selectedCampaign.toUpperCase()}</h2>}
            {isCollapsed && <h2>{selectedCampaign.substring(0, 2).toUpperCase()}</h2>}
          </button>
          <button
            className="sidebar-toggle-btn desktop-only"
            onClick={toggleSidebar}
            aria-label="Toggle sidebar"
          >
            {isCollapsed ? <FiChevronRight size={20} /> : <FiChevronLeft size={20} />}
          </button>
        </div>

        {/* User Info */}
        <div className="sidebar-user">
          <div className="user-avatar">
            {getInitials(displayEmail || displayName)}
          </div>
          {!isCollapsed && (
            <div className="user-info">
              <div className="user-name">{displayName}</div>
              <div className="user-email" style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: '4px' }}>
                {displayEmail}
              </div>
              {userInfo.isGroupMember && userInfo.groupLocationName && (
                <div className="user-group" style={{ fontSize: '0.7rem', color: '#60a5fa', marginTop: '4px', fontWeight: '600' }}>
                  📍 {userInfo.groupLocationName}
                </div>
              )}
              <div className="user-role" style={{ marginTop: '4px' }}>{role}</div>
            </div>
          )}
        </div>

        {/* Navigation */}
        <nav className="sidebar-nav">
          {menuItems.map((item) => (
            <button
              key={item.path}
              className={`nav-item ${isActive(item.path) ? 'active' : ''}`}
              onClick={() => {
                navigate(item.path);
                closeMobileMenu();
              }}
            >
              {(() => {
                const badgeCount = (item.supportBadge && supportUnread > 0) ? supportUnread
                                 : (item.notifBadge  && notifUnread  > 0) ? notifUnread
                                 : 0;
                return (
                  <>
                    <span className="nav-icon" style={{ position: 'relative' }}>
                      {item.icon}
                      {badgeCount > 0 && (
                        <span style={{ position:'absolute', top:-4, right:-6, background:'#ef4444', color:'#fff', borderRadius:'999px', fontSize:'10px', fontWeight:700, minWidth:16, height:16, display:'flex', alignItems:'center', justifyContent:'center', padding:'0 3px', lineHeight:1 }}>{badgeCount}</span>
                      )}
                    </span>
                    {!isCollapsed && <span className="nav-label">{item.label}</span>}
                    {!isCollapsed && badgeCount > 0 && (
                      <span style={{ marginLeft:'auto', background:'#ef4444', color:'#fff', borderRadius:'999px', fontSize:'11px', fontWeight:700, padding:'1px 7px' }}>{badgeCount}</span>
                    )}
                  </>
                );
              })()}
            </button>
          ))}
        </nav>

        {/* Logout Button */}
        <div className="sidebar-footer">
          <button
            className="logout-btn"
            onClick={handleLogout}
          >
            <span className="nav-icon"><FiLogOut /></span>
            {!isCollapsed && <span className="nav-label">Logout</span>}
          </button>
        </div>
      </aside>
    </>
  );
}

export default Sidebar;
